import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import API from "../api/axios";
import {
  MdSecurity, MdSearch,
  MdLogin, MdLogout, MdEdit,
  MdAdd, MdDelete,
} from "react-icons/md";

const actionIcons = {
  LOGIN: <MdLogin size={14} className="text-green-500" />,
  LOGOUT: <MdLogout size={14} className="text-gray-500" />,
  CREATE_DEVICE: <MdAdd size={14} className="text-blue-500" />,
  UPDATE_DEVICE: <MdEdit size={14} className="text-yellow-500" />,
  DELETE_DEVICE: <MdDelete size={14} className="text-red-500" />,
};

const actionColors = {
  LOGIN: "bg-green-100 text-green-700",
  LOGOUT: "bg-gray-100 text-gray-700",
  CREATE_DEVICE: "bg-blue-100 text-blue-700",
  UPDATE_DEVICE: "bg-yellow-100 text-yellow-700",
  DELETE_DEVICE: "bg-red-100 text-red-700",
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 15;

  const fetchLogs = useCallback(async () => {
    try {
      const res = await API.get(`/audit?page=${page}&limit=${limit}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchLogs();
  }, [page, fetchLogs]);

  const filtered = logs.filter(
    (l) =>
      l.username.toLowerCase().includes(search.toLowerCase()) ||
      l.action.toLowerCase().includes(search.toLowerCase()) ||
      l.target.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(total / limit);

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Audit Logs</h2>
        <p className="text-sm text-gray-500">
          Complete activity trail for security and compliance
        </p>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-xl shadow p-4 mb-6 flex items-center gap-4">
        <MdSecurity size={32} className="text-blue-500" />
        <div>
          <p className="text-sm text-gray-500">Total Audit Records</p>
          <p className="text-2xl font-bold text-gray-800">{total}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="relative">
          <MdSearch
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search by user, action, or target..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">
            Loading audit logs...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No audit logs found
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-6 py-3 text-left">Action</th>
                  <th className="px-6 py-3 text-left">User</th>
                  <th className="px-6 py-3 text-left">Target</th>
                  <th className="px-6 py-3 text-left">Details</th>
                  <th className="px-6 py-3 text-left">IP Address</th>
                  <th className="px-6 py-3 text-left">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span
                        className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full w-fit ${
                          actionColors[log.action] ||
                          "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {actionIcons[log.action]}
                        {log.action.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {log.username}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{log.target}</td>
                    <td className="px-6 py-4 text-gray-500 text-xs max-w-xs truncate">
                      {log.details || "—"}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {log.ipAddress || "—"}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {(page - 1) * limit + 1}–
                {Math.min(page * limit, total)} of {total} records
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1}
                  className="px-3 py-1 rounded border text-sm disabled:opacity-40 hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page === totalPages}
                  className="px-3 py-1 rounded border text-sm disabled:opacity-40 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AuditLogs;